import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { Editor } from '@tiptap/core';
import { useReducedMotion } from 'framer-motion';
import { forwardRef, RefObject, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	ActionData,
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionPrivacy,
	ActionTags,
	ActionText,
	Privacy,
	SocialAttachment,
	SocialAttachmentAPIData,
} from '@bonusly/common-action-input';
import {
	Box,
	Button,
	Hide,
	HStack,
	InformationCircleOutline,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Show,
	Tag,
	TagLabel,
	Text,
	Tooltip,
	useBreakpointValue,
	useDisclosure,
	useMediaQuery,
	Wrap,
	WrapItem,
} from '@bonusly/design-system';
import { Recipient, RecipientAPIData } from '@bonusly/recipient-entities';
import { useFeatureFlag, useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { useDialogControl } from '../../../../../lib/dialog';
import { getHttpClient } from '../../../../../lib/http-client';
import { DimensionKey } from '../../../../../queries/recognition-analysis';
import { isEmailIdentifier } from '../../../../../utils/email';
import { shouldStartBizyFlight, shouldStartBizyReducedMotionEntrance, useBizyFlightStore, useReduceBizyMotionPreference } from '../../../../bizy';
import { useBizyContextualSlash } from '../../../../bizy-side-panel/hooks/use-bizy-contextual-slash';
import { useBizySidePanelStore } from '../../../../bizy-side-panel/store/use-bizy-side-panel-store';
import { DraftAssistAccordion } from '../../../gratitude-gauge/draft-assist-accordion';
import { getDraftAssistBizyState } from '../../../gratitude-gauge/draft-assist-bizy-state';
import { DraftAssistPopout } from '../../../gratitude-gauge/draft-assist-popout';
import { useDraftAssist } from '../../../gratitude-gauge/hooks/use-draft-assist';
import { useDraftAssistRetention } from '../../../gratitude-gauge/hooks/use-draft-assist-retention';
import { Meter } from '../../../gratitude-gauge/meter';
import { ScoreBoxAccordion } from '../../../gratitude-gauge/scorebox-accordion';
import { ScoreBoxPopout } from '../../../gratitude-gauge/scorebox-popout';
import { useGratitudeGauge } from '../../../gratitude-gauge/use-gratitude-gauge';
import { GiveBoxAttachment } from '../../events';
import { useGiveBox } from '../../hooks/use-give-box';
import { UseProxyGivingResult } from '../../hooks/use-proxy-giving';
import { loadDraft, saveDraft } from '../../utils/recognition-storage';
import { ProxyGiveButton } from '../proxy-give-button';
import { GiveModalContentBody } from './give-modal-content-body';

// The give-box modal can mount before the destination gauge anchor has
// registered its rect, so flyToGiveBox returns false on the first frame. This
// is most visible on the deep-link open path, where the modal mounts with
// pre-filled content after async recipient resolution. Retry across a bounded
// number of frames (~1s at 60fps) so the flight still starts once the anchor
// exists, rather than leaving both the nav launcher and the gauge bee on
// screen at once.
const MAX_BIZY_FLIGHT_FRAMES = 60;

interface GiveModalContentProps {
	content?: any;
	initialAttachment?: GiveBoxAttachment;
	initialWrapPost?: boolean;
	pendingAiDraft?: { text: string; token: number; autoSend?: boolean };
	onCloseForBizy?: VoidFunction;
	onSuccess: VoidFunction;
	proxyGiving: UseProxyGivingResult;
	setIsSubmitting: (isSubmitting: boolean) => void;
	onVideoAttachmentChange?: (hasVideo: boolean) => void;
	via?: string;
	pendingOutcomeRef?: RefObject<'submitted' | 'discarded' | 'abandoned'>;
}

export interface GiveModalContentHandle {
	hasVideoAttachment: () => boolean;
	hasUserText: () => boolean;
	saveDraft: () => void;
}

const DRAFT_ASSIST_MOBILE_CUTOFF = 1240;
const GAUGE_MOBILE_CUTOFF = 1440;

const normalizeInviteEmail = (value: string) => value.trim().replace(/^@+/, '').toLowerCase();

const isInviteEmailRecipient = (recipient: Recipient): boolean => {
	if (recipient.type === 'group') return false;

	const username = normalizeInviteEmail(recipient.username || '');
	const id = normalizeInviteEmail(recipient.id || '');
	if (recipient.type === 'email') return isEmailIdentifier(username || id);

	// Handles unresolved mentions where type can still be username but id/username is the email.
	return isEmailIdentifier(username) && (id.includes('@') || id === username || !id);
};

export const getInviteEmailRecipients = (action: ActionData): string[] => {
	const recipientEmails = (action.recipients ?? [])
		.filter((recipient) => isInviteEmailRecipient(recipient))
		.map((recipient) => normalizeInviteEmail(recipient.username || recipient.id))
		.filter((email) => isEmailIdentifier(email));

	return Array.from(new Set(recipientEmails));
};

const buildEmailRecipient = (email: string): Recipient =>
	new Recipient({
		id: email,
		display_name: email,
		profile_pic_url: '',
		username: email,
		user_mode: 'email',
	});

/**
 * Resolves paste-created recipient nodes (those without a full `record`) by looking
 * up each username via the autocomplete API and patching the editor node in-place.
 * The resulting `onUpdate` lets `handleUpdate` propagate the real database UUID naturally.
 */
export async function resolveUnresolvedRecipients(editor: Editor, usernames: string[], queryClient: QueryClient) {
	const http = await getHttpClient();
	for (const username of usernames) {
		const normalizedInviteEmail = normalizeInviteEmail(username);
		if (isEmailIdentifier(normalizedInviteEmail)) {
			const recipient = buildEmailRecipient(normalizedInviteEmail);
			editor.commands.command(({ tr, state }) => {
				let found = false;
				state.doc.descendants((node, pos) => {
					if (found) return false;
					if (node.type.name === 'recipient' && node.attrs.id === username && !node.attrs.record) {
						tr.setNodeMarkup(pos, undefined, { id: recipient.username, record: recipient });
						found = true;
						return false;
					}
				});
				return found;
			});
			continue;
		}

		try {
			const res = await queryClient.fetchQuery(['rich-users', { search: username }], () => http.get('/users/autocomplete', { params: { search: username } }), {
				staleTime: 60_000,
			});
			const apiData = res.data.result as RecipientAPIData[];
			const match = apiData.find((r) => new Recipient(r).username === username);
			if (!match) continue;

			const recipient = new Recipient(match);
			editor.commands.command(({ tr, state }) => {
				let found = false;
				state.doc.descendants((node, pos) => {
					if (found) return false;
					if (node.type.name === 'recipient' && node.attrs.id === username && !node.attrs.record) {
						tr.setNodeMarkup(pos, undefined, { id: recipient.username, record: recipient });
						found = true;
						return false;
					}
				});
				return found;
			});
		} catch {
			// Non-critical — username stays as temporary ID until next editor update
		}
	}
}

type EditorNode = {
	type?: string;
	text?: string;
	content?: EditorNode[];
};

const hasNonWhitespaceTextNode = (node: unknown): boolean => {
	if (!node || typeof node !== 'object') {
		return false;
	}

	const editorNode = node as EditorNode;
	if (editorNode.type === 'text' && typeof editorNode.text === 'string' && editorNode.text.trim().length > 0) {
		return true;
	}

	if (!Array.isArray(editorNode.content)) {
		return false;
	}

	return editorNode.content.some(hasNonWhitespaceTextNode);
};

export const GiveModalContent = forwardRef<GiveModalContentHandle, GiveModalContentProps>(
	(
		{
			content,
			initialAttachment,
			initialWrapPost,
			pendingAiDraft,
			onCloseForBizy,
			onSuccess,
			proxyGiving,
			setIsSubmitting,
			onVideoAttachmentChange,
			via,
			pendingOutcomeRef,
		},
		ref
	) => {
		const { t } = useTranslation(['recognition', 'common', 'action_input']);
		const [videoAttachment, setVideoAttachment] = useState<boolean>(false);
		const gratitudeGauge = useGratitudeGauge();
		const inviteByEmailEnabled = useFeatureFlag('plg-invite-by-email');
		const isBizyEnabledForUser = useIsBizyEnabledForUser();
		const isBizyPanelOpen = useBizySidePanelStore((state) => state.isOpen);
		const rewardDialog = useDialogControl('reward');
		const { track } = useAnalytics();
		const queryClient = useQueryClient();
		const [isDraftAssistDesktop] = useMediaQuery(`(min-width: ${DRAFT_ASSIST_MOBILE_CUTOFF}px)`);
		const [reduceBizyMotion] = useReduceBizyMotionPreference();
		const shouldReduceMotion = useReducedMotion();

		const [recipients, setRecipients] = useState<{ id: string; name: string }[]>([]);
		const [attachedValues, setAttachedValues] = useState<string[]>([]);
		// Starts as false; DraftAssistPopout and DraftAssistAccordion only call onOpenChange on
		// user interaction, never on mount, and they always start closed (no localStorage restore).
		const [isGaugeOpen, setIsGaugeOpen] = useState(false);
		const [requestedDimensionKey, setRequestedDimensionKey] = useState<DimensionKey | null>(null);
		const hasRequestedBizyFlightRef = useRef(false);

		const isMobile = useBreakpointValue({ base: true, sm: false });

		const draftAssist = useDraftAssist({
			dimensions: gratitudeGauge.dimensions,
			draftText: gratitudeGauge.text,
			recipientIds: recipients.map((r) => r.id),
			attachedValues,
			isScoring: gratitudeGauge.isFetching,
			// LLM fires only when the user explicitly opens the panel. Both components start
			// closed when draft assist is on (no localStorage restore), so isGaugeOpen and
			// the child open state are always in sync — no mount-time desync possible.
			isOpen: isGaugeOpen,
			requestedDimensionKey,
		});
		const flyToGiveBox = useBizyFlightStore((state) => state.flyToGiveBox);
		const setParkedState = useBizyFlightStore((state) => state.setParkedState);
		const draftAssistBizyState = getDraftAssistBizyState({ isLoading: draftAssist.isLoading, isScoring: gratitudeGauge.isFetching, isOpen: isGaugeOpen });
		const shouldUseReducedMotionEntrance = shouldStartBizyReducedMotionEntrance({
			isBizyEnabled: isBizyEnabledForUser,
			isDraftAssistDesktop,
			isBizyPanelOpen,
			reduceBizyMotion,
			shouldReduceMotion: !!shouldReduceMotion,
		});
		const shouldUseBizyFlight =
			!rewardDialog.isOpen &&
			(shouldUseReducedMotionEntrance ||
				shouldStartBizyFlight({
					isBizyEnabled: isBizyEnabledForUser,
					isDraftAssistDesktop,
					reduceBizyMotion,
					shouldReduceMotion: !!shouldReduceMotion,
				}));

		useEffect(() => {
			if (!shouldUseBizyFlight || hasRequestedBizyFlightRef.current) return;

			let frame = 0;
			let framesRemaining = MAX_BIZY_FLIGHT_FRAMES;
			let cancelled = false;

			const attemptFlight = () => {
				if (cancelled) return;

				if (flyToGiveBox(draftAssistBizyState, { source: isBizyPanelOpen ? 'preferred' : 'offscreenRight' })) {
					hasRequestedBizyFlightRef.current = true;
					return;
				}

				if (--framesRemaining > 0) {
					frame = requestAnimationFrame(attemptFlight);
				}
			};

			frame = requestAnimationFrame(attemptFlight);

			return () => {
				cancelled = true;
				cancelAnimationFrame(frame);
			};
		}, [draftAssistBizyState, flyToGiveBox, isBizyPanelOpen, shouldUseBizyFlight]);

		useEffect(() => {
			if (shouldUseBizyFlight || !hasRequestedBizyFlightRef.current) return;

			useBizyFlightStore.getState().clearFlight();
			hasRequestedBizyFlightRef.current = false;
		}, [shouldUseBizyFlight]);

		useEffect(() => {
			if (hasRequestedBizyFlightRef.current) {
				setParkedState(draftAssistBizyState);
			}
		}, [draftAssistBizyState, setParkedState]);

		useEffect(() => {
			return () => {
				if (!hasRequestedBizyFlightRef.current) return;

				if (!useBizyFlightStore.getState().flyBackToSource()) {
					useBizyFlightStore.getState().clearFlight();
				}
			};
		}, []);

		const onRecognitionSuccessInnerRef = useRef<VoidFunction>(() => {});

		const config = useGiveBox({
			onSuccess: () => onRecognitionSuccessInnerRef.current(),
			via,
			draftAssistInteractionIds: draftAssist.draftAssistInteractionIds,
			proxyGiving,
			initialWrapPost,
		});
		const inviteConfirmationModal = useDisclosure();
		const [pendingInviteAction, setPendingInviteAction] = useState<ActionData | null>(null);
		const [inviteEmails, setInviteEmails] = useState<string[]>([]);

		const closeInviteConfirmation = useCallback(() => {
			setPendingInviteAction(null);
			inviteConfirmationModal.onClose();
		}, [inviteConfirmationModal]);

		const submitWithInviteConfirmation = useCallback(
			(action: ActionData) => {
				const inviteRecipients = getInviteEmailRecipients(action);
				if (!inviteByEmailEnabled && inviteRecipients.length > 0) {
					config.ref.current?.error(
						t('bonus_input.invite_confirmation.invite_by_email_disabled', {
							defaultValue: 'Inviting by email is currently unavailable. Remove email recipients to continue.',
							ns: 'recognition',
						})
					);
					return;
				}

				if (inviteRecipients.length === 0) {
					config.onSubmit(action);
					if (inviteConfirmationModal.isOpen) {
						closeInviteConfirmation();
					}
					return;
				}

				setPendingInviteAction(action);
				setInviteEmails(Array.from(new Set(inviteRecipients)));
				inviteConfirmationModal.onOpen();
			},
			[closeInviteConfirmation, config, inviteByEmailEnabled, inviteConfirmationModal, t]
		);

		const handleInviteConfirmSubmit = useCallback(() => {
			if (!pendingInviteAction) {
				closeInviteConfirmation();
				return;
			}

			// Safety check only. This should not happen because the modal only opens
			// when there are invite-email recipients in the recognition composer.
			if (inviteEmails.length === 0) {
				closeInviteConfirmation();
				return;
			}

			const nonEmailRecipients = (pendingInviteAction.recipients ?? []).filter((recipient) => !isInviteEmailRecipient(recipient));
			const mergedEmailRecipients = inviteEmails.map(buildEmailRecipient);

			config.onSubmit({
				...pendingInviteAction,
				recipients: [...nonEmailRecipients, ...mergedEmailRecipients],
			});
		}, [closeInviteConfirmation, config, inviteEmails, pendingInviteAction]);

		const { insertSuggestion: insertDraftAssistSuggestion, reportRetention } = useDraftAssistRetention({
			draftAssist,
			inputRef: config.ref,
		});

		const handleRecognitionSuccess = useCallback(() => {
			closeInviteConfirmation();
			reportRetention();
			void draftAssist.signalOutcome('submitted');
			onSuccess();
		}, [closeInviteConfirmation, reportRetention, draftAssist.signalOutcome, onSuccess]);

		onRecognitionSuccessInnerRef.current = handleRecognitionSuccess;

		const handleGaugeOpenChange = useCallback(
			(nextIsOpen: boolean) => {
				if (nextIsOpen && draftAssist.hasUnmetDimension) {
					track(AnalyticsEvent.GratitudeGauge.DraftAssistHintClicked, {
						dimensionKey: draftAssist.hintDimensionKey,
					});
				}
				setIsGaugeOpen(nextIsOpen);
				if (!nextIsOpen) {
					setRequestedDimensionKey(null);
				}
			},
			[draftAssist.hasUnmetDimension, draftAssist.hintDimensionKey, track]
		);

		const handleDimensionExpanded = useCallback((key: string | null) => {
			setRequestedDimensionKey(key as DimensionKey | null);
		}, []);

		// HABITS-1294: intercept `/` as the first character to open Bizy
		// contextually with recipients + selected values, so users can draft
		// without leaving the give box. The give box is a modal, so we close
		// it when `/` fires (so the Bizy side panel is unobscured). On
		// Insert / Give now, the UI block dispatcher in
		// `bizy-side-panel/lib/ui-block-action-dispatcher.ts` fires
		// `GiveBoxEvent.Open` with the drafted text — which lands here as
		// `pendingAiDraft` and is inserted with the AI-touched highlight by
		// the effect below.
		const recipientsRef = useRef(recipients);
		recipientsRef.current = recipients;
		const { handleKeyDownCapture: handleBizySlashKeyDown } = useBizyContextualSlash({
			surface: 'recognition',
			enabled: isBizyEnabledForUser,
			getInputHandle: () => config.ref.current,
			getSurfaceData: () => {
				const data = config.ref.current?.data();
				return {
					recipientIds: recipientsRef.current.map((r) => r.id),
					recipientNames: recipientsRef.current.map((r) => r.name),
					amount: data?.amount ?? null,
					hashtags: data?.hashtags ?? [],
				};
			},
			getRecipientLabel: () => {
				const names = recipientsRef.current.map((r) => r.name);
				if (names.length === 0) return undefined;
				if (names.length === 1) return names[0];
				if (names.length === 2) return `${names[0]} and ${names[1]}`;
				return `${names[0]} and ${names.length - 1} others`;
			},
			onInvoke: () => {
				// Hand off to Bizy — close the modal in a draft-preserving
				// way so recipients / hashtags / amount survive and the
				// subsequent re-open (triggered by Insert) restores them.
				onCloseForBizy?.();
			},
			onDismiss: () => {
				// Modal is closed; there is no live input to refocus. No-op.
			},
		});

		// When the modal re-opens with a pending Bizy draft (set on `init-give-recognition`),
		// insert the text with the AI-touched highlight once the editor is ready. Using the
		// token as dep lets us re-trigger on subsequent Insert → open cycles too.
		const appliedAiDraftTokenRef = useRef<number | undefined>(undefined);
		useEffect(() => {
			if (!pendingAiDraft) return;
			if (appliedAiDraftTokenRef.current === pendingAiDraft.token) return;
			const input = config.ref.current;
			if (!input) return;
			appliedAiDraftTokenRef.current = pendingAiDraft.token;
			input.insertWithHighlight(pendingAiDraft.text);
			input.focus();

			// Bizy "Give now" (recognition_draft_card's send_now action) routes
			// through the give-box + autoSend so we reuse the same submit path
			// the manual Give button uses (validation, balance check, error
			// surfacing). Two rAFs give the editor a beat to commit the
			// insertion + recompute derived state before we pull `data()`.
			if (pendingAiDraft.autoSend) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						const live = config.ref.current;
						if (!live) return;
						const data = live.data();
						if (!data || !data.text || data.text.trim().length === 0) return;
						submitWithInviteConfirmation(data);
					});
				});
			}
		}, [pendingAiDraft, config.ref, submitWithInviteConfirmation]);

		// Track the latest editor content for draft preservation
		const editorContentRef = useRef<unknown | null>(null);
		// Load draft on mount (only if no explicit content was passed via props)
		// This handles restoring state after accidental modal dismissal
		const draft = useMemo(() => {
			if (!content && !initialAttachment) {
				return loadDraft();
			}
			return null;
			// eslint-disable-next-line react-hooks/exhaustive-deps -- only check on mount
		}, []);

		// Compute effective values: use draft if available, otherwise use props
		const effectiveContent = draft?.content ?? content;
		const effectiveAttachment = useMemo(() => {
			if (initialAttachment) return initialAttachment;
			if (draft?.attachment) {
				return new SocialAttachment(draft.attachment as SocialAttachmentAPIData);
			}
			return undefined;
		}, [draft?.attachment, initialAttachment]);
		const effectivePrivacy = (draft?.privacy as Privacy) ?? undefined;

		// Initialize editorContentRef with loaded content so drafts can be re-saved
		// even if the user doesn't make any edits (refreshes the expiration timer)
		useEffect(() => {
			if (effectiveContent && !editorContentRef.current) {
				editorContentRef.current = effectiveContent;
			}
		}, [effectiveContent]);

		// this hook allows us to expose data to parent compoenents. Specifically, they need to know
		// if there is a video attachment so they can confirm the user wants to delete it before closing
		// the modal. Also exposes saveDraft for overlay click handling.
		useImperativeHandle(
			ref,
			() => ({
				hasVideoAttachment: () => videoAttachment,
				hasUserText: () => hasNonWhitespaceTextNode(editorContentRef.current),
				saveDraft: () => {
					const editorContent = editorContentRef.current;
					if (!editorContent) return;

					const data = config.ref.current?.data();
					let attachment: unknown = undefined;

					// Only save GIF attachments (social), not videos
					if (data?.attachment && data.attachment.category === 'social') {
						attachment = data.attachment.toJSON();
					}

					saveDraft(editorContent, attachment, data?.privacy);
				},
			}),
			[config.ref, videoAttachment]
		);

		useEffect(() => {
			return () => {
				// Read the outcome the parent committed to before triggering the close animation.
				// Falls back to 'abandoned' when no parent ref is provided (e.g. standalone usage).
				void draftAssist.signalOutcome(pendingOutcomeRef?.current ?? 'abandoned');
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs on unmount only
		}, []);

		useEffect(() => {
			setIsSubmitting(config.isSubmitting);
		}, [config.isSubmitting, setIsSubmitting]);

		// The `checkVideoAttachment` function is needed to maintain the state of the video attachment within the GiveModalContent component.
		// It serves the following purposes:
		// 1. State Synchronization: It ensures that the `videoAttachment` state is always in sync with the actual data present in the ActionInput component.
		//    This is important because the presence of a video attachment affects the behavior of the modal, particularly when the user attempts to close it.
		// 2. User Confirmation: By detecting changes in the video attachment state, it allows the parent component to prompt the user for confirmation
		//    before closing the modal if a video is attached. This prevents accidental loss of data, which is especially important for video files that
		//    might take significant time and resources to upload.
		// 3. Real-time Updates: The function is called immediately and at regular intervals to check for changes in the attachment state.
		//    This real-time checking is necessary because the attachment state can change due to user actions or asynchronous operations.
		// 4. Callback Invocation: It invokes the `onVideoAttachmentChange` callback whenever there is a change in the video attachment state.
		//    This allows other components or hooks to react to changes, enabling a more dynamic and responsive UI.
		const checkVideoAttachment = useCallback(() => {
			const data = config.ref.current?.data();
			const category = data?.attachment?.category;
			const hasVideoOrAudio = category === 'video' || category === 'audio';

			// Ughh I don't like doing this at all. We really need to fix the state management with the give box
			if (videoAttachment && !hasVideoOrAudio) {
				gratitudeGauge.onChangeTranscription('');
			}

			if (hasVideoOrAudio !== videoAttachment) {
				setVideoAttachment(hasVideoOrAudio);
				onVideoAttachmentChange?.(hasVideoOrAudio);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps -- gratitudeGauge returns unstable object reference, intentionally omitted
		}, [config.ref, onVideoAttachmentChange, videoAttachment]);

		useEffect(() => {
			// Check immediately
			checkVideoAttachment();
			// Set up an interval to check for changes. We're operation outside
			// the ActionMedia provider and don't have access to media state.
			const interval = setInterval(checkVideoAttachment, 100);
			// Cleanup the interval when the component unmounts
			return () => clearInterval(interval);
		}, [checkVideoAttachment]);

		// Sync any recipients already present in restored draft content.
		// onUpdate doesn't fire for initial content, so we read once on mount.
		useEffect(() => {
			const data = config.ref.current?.data();
			if (!data) return;
			const initial = data.recipients.map((r) => ({ id: r.id, name: r.displayName }));
			if (initial.length > 0) setRecipients(initial);
			// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs once on mount
		}, []);

		const draftAssistProp = useMemo(() => {
			const suggestion = draftAssist.suggestion ?? undefined;
			return {
				suggestion,
				dimensionKey: draftAssist.dimensionKey ?? undefined,
				isLoading: draftAssist.isLoading,
				loadingDimensionKey: requestedDimensionKey,
				conversationIdRef: draftAssist.conversationIdRef,
				onAdd: suggestion ? () => insertDraftAssistSuggestion(suggestion) : undefined,
				onRegenerate: draftAssist.regenerate,
			};
		}, [
			draftAssist.suggestion,
			draftAssist.dimensionKey,
			draftAssist.isLoading,
			draftAssist.regenerate,
			draftAssist.conversationIdRef,
			insertDraftAssistSuggestion,
			requestedDimensionKey,
		]);

		const handleUpdate = ({ editor }: { editor: Editor }) => {
			const rawText = editor.getText();
			gratitudeGauge.onChangeText(rawText.trim());
			// Store the latest editor content for draft preservation
			editorContentRef.current = editor.getJSON();

			// Read recipients directly from the TipTap doc — same pattern as hashtags below.
			// config.ref.current?.data() relies on ActionInput's inputState which lags one render
			// behind onUpdate. For paste-created recipient nodes the node has record=null (only
			// attrs.id = username), causing r.id to be undefined and the key comparison to be a
			// no-op. Reading from the doc avoids the stale-state issue and lets us detect
			// unresolved nodes immediately.
			const nextRecipients: { id: string; name: string }[] = [];
			const unresolvedUsernames: string[] = [];
			editor.view.state.doc.descendants((node) => {
				if (node.type.name !== 'recipient') return;
				if (node.attrs.record?.id) {
					// Fully resolved (autocomplete selection) — use real database UUID
					nextRecipients.push({ id: node.attrs.record.id, name: node.attrs.record.displayName });
				} else if (node.attrs.id) {
					// Paste-created: record is null, username is the only identifier available.
					// Use username as a temporary key so the recipient gates in useDraftAssist
					// are unblocked immediately, then resolve asynchronously below.
					nextRecipients.push({ id: node.attrs.id, name: node.attrs.id });
					unresolvedUsernames.push(node.attrs.id);
				}
			});
			setRecipients((prev) => {
				const prevKey = prev.map((r) => r.id).join(',');
				const nextKey = nextRecipients.map((r) => r.id).join(',');
				return prevKey === nextKey ? prev : nextRecipients;
			});
			if (unresolvedUsernames.length > 0) {
				void resolveUnresolvedRecipients(editor, unresolvedUsernames, queryClient);
			}

			// Read hashtags directly from the current editor document. config.ref.current?.data()
			// returns inputState from ActionInput's previous render — TipTap fires onUpdate before
			// ActionInput re-renders, so that path always lags one change behind.
			const nextHashtags: string[] = [];
			editor.view.state.doc.descendants((node) => {
				if (node.type.name === 'hashtag') nextHashtags.push(node.attrs.id as string);
			});
			setAttachedValues((prev) => {
				const prevKey = prev.join(',');
				const nextKey = nextHashtags.join(',');
				return prevKey === nextKey ? prev : nextHashtags;
			});
		};

		const mobileBreakpoint = isBizyEnabledForUser ? DRAFT_ASSIST_MOBILE_CUTOFF : GAUGE_MOBILE_CUTOFF;

		return (
			<ActionInput
				autofocus="end"
				content={effectiveContent}
				initialAttachment={effectiveAttachment}
				initialPrivacy={effectivePrivacy}
				onUpdate={handleUpdate}
				{...config}
				onSubmit={submitWithInviteConfirmation}
			>
				<GiveModalContentBody display="flex" flexDirection="column" flex="1" px={{ base: 0, sm: 6 }} pt={{ base: 4, sm: 0 }} pb={0}>
					<Show breakpoint={`(min-width: ${mobileBreakpoint}px)`}>
						{isBizyEnabledForUser ? (
							<DraftAssistPopout
								dimensions={gratitudeGauge.dimensions}
								isLoading={draftAssist.isLoading}
								isScoring={gratitudeGauge.isFetching}
								text={gratitudeGauge.text}
								draftAssist={draftAssistProp}
								hasUnmetDimension={draftAssist.hasUnmetDimension}
								hintDimensionKey={draftAssist.hintDimensionKey}
								recipientCount={recipients.length}
								onOpenChange={handleGaugeOpenChange}
								onDimensionExpanded={handleDimensionExpanded}
							/>
						) : (
							<ScoreBoxPopout
								dimensions={gratitudeGauge.dimensions}
								isLoading={gratitudeGauge.isFetching}
								text={gratitudeGauge.text}
								onOpenChange={setIsGaugeOpen}
							/>
						)}
					</Show>

					{/* Visibility dropdown at the top - same location for mobile and desktop */}
					<Box px={{ base: 4, sm: 0 }}>
						<ActionPrivacy />
					</Box>

					{/* Text input area */}
					<Box flex="1" position="relative" display="flex" flexDirection="column" px={{ base: 4, sm: 0 }} onKeyDownCapture={handleBizySlashKeyDown}>
						<ActionText
							height="100%"
							inputStyles={
								isMobile
									? {
											flex: '1',
											minHeight: rem(80),
											display: 'flex',
											flexDirection: 'column',
											position: 'relative',
											padding: '0.5rem 0',
										}
									: { minHeight: rem(120), padding: '0.5rem 0' }
							}
							display="flex"
							flexDirection="column"
							flex="1"
							minHeight={0}
							overflow="hidden"
						/>
						<ActionFeedback mb={2} />
						<ActionMedia w="full" flexShrink={0} />
					</Box>
				</GiveModalContentBody>

				<ModalFooter display="block" px={{ base: 0, sm: 6 }} py={4} overflow="hidden" borderBottomRadius="10px">
					{/* Desktop: Bottom toolbar - Tags on left, Action buttons on right */}
					<HStack justifyContent="space-between" mb={2} gap={2} px={{ base: 4, md: 0 }}>
						<ActionTags />
						<ActionDecorations />
					</HStack>

					<Box px={{ base: 4, sm: 0 }}>
						<ProxyGiveButton proxyGiving={config.proxyGiving} />
					</Box>

					<Hide breakpoint={`(min-width: ${mobileBreakpoint}px)`}>
						<Box px={{ base: 4, sm: 0 }} pt={2} pb={2}>
							<Box borderRadius="md" overflow="hidden" borderWidth="1px" borderColor="border.default">
								{isBizyEnabledForUser ? (
									<DraftAssistAccordion
										dimensions={gratitudeGauge.dimensions}
										isLoading={draftAssist.isLoading}
										isScoring={gratitudeGauge.isFetching}
										text={gratitudeGauge.text}
										draftAssist={draftAssistProp}
										hasUnmetDimension={draftAssist.hasUnmetDimension}
										hintDimensionKey={draftAssist.hintDimensionKey}
										recipientCount={recipients.length}
										onOpenChange={handleGaugeOpenChange}
										onDimensionExpanded={handleDimensionExpanded}
									/>
								) : (
									<ScoreBoxAccordion
										dimensions={gratitudeGauge.dimensions}
										isLoading={gratitudeGauge.isFetching}
										text={gratitudeGauge.text}
										onOpenChange={setIsGaugeOpen}
									/>
								)}
							</Box>
						</Box>
					</Hide>

					<Show breakpoint={`(min-width: ${mobileBreakpoint}px)`}>
						<Box mx={-6} mb={-4} mt={2}>
							<Meter matches={videoAttachment ? gratitudeGauge.totalDimensions : gratitudeGauge.totalMatches} total={gratitudeGauge.totalDimensions} />
						</Box>
					</Show>
				</ModalFooter>
				<Modal
					isOpen={inviteConfirmationModal.isOpen}
					onClose={closeInviteConfirmation}
					size={{ base: 'full', sm: 'xl' }}
					closeOnOverlayClick={!config.isSubmitting}
				>
					<ModalOverlay />
					<ModalContent>
						<ModalHeader>{t('bonus_input.invite_confirmation.title')}</ModalHeader>
						<ModalCloseButton />
						<ModalBody>
							<Text color="text.default" fontSize="md" lineHeight={6} mb={4}>
								{t('bonus_input.invite_confirmation.description')}
							</Text>
							<HStack alignItems="center" spacing={1} mb={2}>
								<Text color="text.default" fontSize="sm" fontWeight="medium">
									{t('bonus_input.invite_confirmation.review_list_label', {
										defaultValue: 'Teammates to invite',
									})}
								</Text>
								<Tooltip
									label={t('bonus_input.invite_confirmation.permissions_tooltip', {
										defaultValue: 'All invited users will have standard permissions to start.',
									})}
									placement="top"
								>
									<Box as="span" display="inline-flex" alignItems="center" cursor="pointer" color="text.softest">
										<InformationCircleOutline size="sm" />
									</Box>
								</Tooltip>
							</HStack>
							<Wrap spacing={2}>
								{inviteEmails.map((email) => (
									<WrapItem key={email}>
										<Tag size="sm" borderRadius="full" variant="subtle" bg="primary.bgAlpha" color="text.interactive" fontWeight="medium" px={2} py={1}>
											<TagLabel>{email}</TagLabel>
										</Tag>
									</WrapItem>
								))}
							</Wrap>
						</ModalBody>
						<ModalFooter justifyContent="space-between">
							<Button colorScheme="secondary" variant="subtle" onClick={closeInviteConfirmation}>
								{t('cancel', { ns: 'common' })}
							</Button>
							<Button isLoading={config.isSubmitting} onClick={handleInviteConfirmSubmit}>
								{t('bonus_input.invite_confirmation.submit')}
							</Button>
						</ModalFooter>
					</ModalContent>
				</Modal>
			</ActionInput>
		);
	}
);

GiveModalContent.displayName = 'GiveModalContent';
